import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { getPricingSettings, type CreditPackage } from "@/lib/pricingApi";

const PricingSection = () => {
  const { t } = useLanguage();
  const [creditPackages, setCreditPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPricing();
  }, []);

  const loadPricing = async () => {
    try {
      setLoading(true);
      const settings = await getPricingSettings();
      // Show only first 4 packages for home page
      setCreditPackages(settings.packages.slice(0, 4));
    } catch (error) {
      console.error("Failed to load pricing:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-24 relative">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-heading font-bold mb-4">
            {t("ราคา", "Simple")} <span className="text-gradient">{t("เรียบง่าย", "Pricing")}</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t("จ่ายตามการใช้งาน ไม่มีสมัครสมาชิก ใช้เท่าที่ต้องการ", "Pay-as-you-go credits. No subscriptions. Use what you need.")}
          </p>
        </div>

        {/* Pricing Grid */}
        {loading ? (
          <div className="flex justify-center items-center min-h-[300px]">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {creditPackages.map((pkg, index) => (
              <Card
              key={index}
              variant={pkg.popular ? "glow" : "glass"}
              className={`relative ${pkg.popular ? "border-primary/50 scale-105" : ""}`}
            >
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-3 py-1 bg-gradient-primary text-primary-foreground text-xs font-semibold rounded-full flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    {t("ยอดนิยม", "Popular")}
                  </span>
                </div>
              )}
              <CardHeader className="text-center pb-2">
                <CardTitle className="text-4xl font-bold text-gradient">
                  {pkg.credits.toLocaleString()}
                </CardTitle>
                <p className="text-muted-foreground">{t("เครดิต", "Credits")}</p>
              </CardHeader>
              <CardContent className="text-center">
                <div className="mb-6">
                  <span className="text-3xl font-bold">฿{pkg.price.toLocaleString()}</span>
                </div>
                <ul className="space-y-2 text-sm text-muted-foreground mb-6">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-primary" />
                    {t("ใช้ได้ทุกโมเดล AI", "All AI Models")}
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-primary" />
                    {pkg.credits >= 10000
                      ? t("ไม่มีวันหมดอายุ", "No Expiration")
                      : t("เครดิตอายุ 2 ปี", "Expires in 2 years")}
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-primary" />
                    {t("ดาวน์โหลดประวัติ", "Download History")}
                  </li>
                </ul>
                <Link to="/pricing">
                  <Button
                    variant={pkg.popular ? "hero" : "outline"}
                    className="w-full"
                  >
                    {t("ซื้อเครดิต", "Buy Credits")}
                  </Button>
                </Link>
              </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Credit Usage Info */}
        <div className="mt-16 max-w-4xl mx-auto">
          <Card variant="glass">
            <CardContent className="p-8">
              <h3 className="text-xl font-heading font-semibold mb-6 text-center">
                {t("คู่มือการใช้เครดิต", "Credit Usage Guide")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
                <div className="text-center p-4 rounded-lg bg-muted/30">
                  <div className="text-2xl font-bold text-cyan-500 mb-2">3.5-4</div>
                  <p className="text-muted-foreground">{t("เครดิตต่อภาพ", "Credits per Photo")}</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-muted/30">
                  <div className="text-2xl font-bold text-purple-500 mb-2">30-60</div>
                  <p className="text-muted-foreground">{t("เครดิตต่อวิดีโอ", "Credits per Video")}</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-muted/30">
                  <div className="text-2xl font-bold text-magenta-500 mb-2">12</div>
                  <p className="text-muted-foreground">{t("เครดิตต่อเพลง", "Credits per Music")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
